'use client';

import { BottomSheetModal } from '@frontend/components/BottomSheetModal';
import { safeWindow } from '@frontend/utils/safeWindow';
import { MOBILE_QUERY, useMediaQuery } from '@frontend/utils/UseMediaQuery';
import { Dialog, Popover, VisuallyHidden } from '@radix-ui/themes';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { DialogHeader } from './DialogHeader';
import { FullScreenDialog } from './FullScreenDialog';

export interface ResponsiveModalProps {
  children: React.ReactNode;
  trigger?: React.ReactNode;
  popoverClassName?: string;
  popoverProps?: React.ComponentProps<typeof Popover.Content>;
  shouldOpen?: boolean;

  desktopVariant?: 'dialog' | 'popover';
  mobileVariant?: 'bottomSheet' | 'fullScreen' | 'floatingIsland';
  title?: string;
  topRightContent?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dialogContentProps?: React.ComponentProps<typeof Dialog.Content>;
  hasPadding?: boolean;
  lockBodyScroll?: boolean;
  showClose?: boolean;
}

// Creates a reusable component that behaves differently based on device type:
// - If desktop, display a popup
// - If mobile, display a bottom sheet
const ResponsiveModal = ({
  children,
  trigger,
  popoverClassName,
  popoverProps,
  shouldOpen = true,
  desktopVariant,
  mobileVariant = 'bottomSheet',
  title,
  topRightContent,
  open,
  onOpenChange,
  dialogContentProps,
  hasPadding = true,
  lockBodyScroll = false,
  showClose = true,
}: ResponsiveModalProps) => {
  const [isOpen, setIsOpen] = useState(open || false);
  const isMobile = useMediaQuery(MOBILE_QUERY);

  if (isMobile) {
    if (mobileVariant === 'fullScreen') {
      return (
        <>
          <div onPointerUp={() => setIsOpen(true)}>{trigger}</div>
          <FullScreenDialog
            open={isOpen}
            onOpenChange={setIsOpen}
            title={title}
            topRightContent={topRightContent}
            hasPadding={hasPadding}
            lockBodyScroll={lockBodyScroll}
            showClose={showClose}
          >
            {children}
          </FullScreenDialog>
        </>
      );
    }

    if (mobileVariant === 'floatingIsland') {
      return (
        <FloatingIslandModal
          open={open}
          onOpenChange={onOpenChange}
          title={title}
          showClose={showClose}
        >
          {children}
        </FloatingIslandModal>
      );
    }

    return (
      <BottomSheetModal
        trigger={trigger}
        shouldOpen={shouldOpen}
        open={open}
        onOpenChange={onOpenChange}
        showClose={showClose}
        title={title}
        topRightContent={topRightContent}
      >
        {children}
      </BottomSheetModal>
    );
  }

  if (desktopVariant === 'dialog') {
    return (
      <DialogModal
        trigger={trigger}
        title={title}
        topRightContent={topRightContent}
        open={open}
        onOpenChange={onOpenChange}
        dialogContentProps={dialogContentProps}
        showClose={showClose}
      >
        {children}
      </DialogModal>
    );
  }

  return (
    <PopoverModal
      trigger={trigger}
      popoverClassName={popoverClassName}
      popoverProps={popoverProps}
      shouldOpen={shouldOpen}
      open={open}
      onOpenChange={onOpenChange}
      showClose={showClose}
    >
      {children}
    </PopoverModal>
  );
};

// DialogModal supports both controlled and uncontrolled modes:
// - Controlled: when `open` prop is provided, the parent component manages the open state
// - Uncontrolled: when `open` prop is not provided, the component manages its own state
const DialogModal = ({
  children,
  trigger,
  title,
  topRightContent,
  open,
  onOpenChange,
  dialogContentProps,
  showClose,
}: ResponsiveModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <div>{trigger}</div>
      </Dialog.Trigger>
      <Dialog.Content
        className="!pt-0"
        maxWidth="450px"
        {...dialogContentProps}
      >
        <VisuallyHidden>
          <Dialog.Title></Dialog.Title>
        </VisuallyHidden>
        <DialogHeader
          title={title}
          showClose={showClose}
          onClose={() => handleOpenChange(false)}
          hasPadding={true}
          topRightContent={topRightContent}
        />
        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
};

const PopoverModal = ({
  children,
  trigger,
  popoverClassName,
  popoverProps,
  shouldOpen,
  open,
  onOpenChange,
}: ResponsiveModalProps & { shouldOpen: boolean }) => {
  return (
    <Popover.Root
      modal
      {...popoverProps}
      open={open}
      onOpenChange={onOpenChange}
    >
      {trigger && (
        <Popover.Trigger
          onClick={(e) => {
            e.stopPropagation();
            if (!shouldOpen) {
              e.preventDefault();
            }
          }}
        >
          {trigger}
        </Popover.Trigger>
      )}
      <Popover.Content
        {...popoverProps}
        style={{
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--gray-4)',
          boxShadow: 'var(--shadow-5)',
        }}
        className={popoverClassName}
      >
        {children}
      </Popover.Content>
    </Popover.Root>
  );
};

const FloatingIslandModal = ({
  children,
  open,
  onOpenChange,
  title,
  showClose = true,
}: Pick<
  ResponsiveModalProps,
  'children' | 'open' | 'onOpenChange' | 'title' | 'showClose'
>) => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (typeof safeWindow !== 'undefined') {
        const viewportHeight =
          safeWindow.visualViewport?.height || safeWindow.innerHeight;
        const windowHeight = safeWindow.innerHeight;
        const heightDifference = windowHeight - viewportHeight;
        setKeyboardHeight(heightDifference > 0 ? heightDifference : 0);
      }
    };

    if (typeof safeWindow !== 'undefined' && safeWindow.visualViewport) {
      safeWindow.visualViewport.addEventListener('resize', handleResize);
      return () =>
        safeWindow?.visualViewport?.removeEventListener('resize', handleResize);
    }
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[99999]"
            onClick={() => {
              return onOpenChange?.(false);
            }}
          />

          {/* Floating Island */}
          <motion.div
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{
              y: keyboardHeight > 0 ? -keyboardHeight + 16 : 0,
              opacity: 1,
              scale: 1,
            }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              duration: 0.3,
            }}
            className="fixed bottom-4 left-4 right-4 z-[100000] mx-auto max-w-md"
          >
            <div
              className="rounded-3xl shadow-2xl backdrop-blur-lg border"
              style={{
                backgroundColor: 'var(--color-background)',
                border: '1px solid var(--gray-4)',
                boxShadow: 'var(--shadow-5)',
              }}
            >
              {title && showClose && (
                <DialogHeader
                  title={title}
                  showClose={showClose}
                  onClose={() => onOpenChange?.(false)}
                  hasPadding={true}
                />
              )}
              <div className="p-6">{children}</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ResponsiveModal;
